"use client";

import Link from "next/link";

import { getOrgOrTeamAvatar } from "@calcom/lib/defaultAvatarImage";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Avatar } from "@calcom/ui/components/avatar";
import { Badge } from "@calcom/ui/components/badge";
import { Icon } from "@calcom/ui/components/icon";

export type OrgPublicViewData = {
  name: string;
  slug: string;
  logoUrl: string | null;
  bio: string | null;
  teams: {
    name: string;
    slug: string | null;
    logoUrl: string | null;
    bio: string | null;
    eventTypes: {
      slug: string;
      title: string;
      description: string | null;
      length: number;
      schedulingType: string | null;
    }[];
  }[];
};

export default function OrgPublicView({ org }: { org: OrgPublicViewData }) {
  const { t } = useLocale();
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8">
      <div className="mb-8 flex flex-col items-center text-center">
        <Avatar
          alt={org.name}
          imageSrc={getOrgOrTeamAvatar(org) ?? undefined}
          size="xl"
          className="mb-4"
        />
        <h1 className="font-cal mb-1 text-2xl font-semibold">{org.name}</h1>
        {org.bio && <p className="text-subtle text-sm">{org.bio}</p>}
      </div>

      {org.teams.length === 0 ? (
        <p className="text-subtle text-center text-sm">{t("no_public_teams_in_org")}</p>
      ) : (
        <section>
          <h2 className="text-emphasis mb-3 text-sm font-medium uppercase tracking-wide">
            {t("teams")}
          </h2>
          <ul className="space-y-4">
            {org.teams.map((team) => (
              <li key={team.slug ?? team.name} className="border-subtle bg-default overflow-hidden rounded-md border">
                <Link
                  href={`/team/${team.slug}`}
                  className="hover:bg-subtle flex items-center gap-3 px-4 py-3 transition-colors">
                  <Avatar
                    alt={team.name}
                    imageSrc={getOrgOrTeamAvatar(team) ?? undefined}
                    size="sm"
                  />
                  <span className="min-w-0">
                    <span className="text-emphasis block truncate font-medium">{team.name}</span>
                    {team.bio && (
                      <span className="text-subtle block truncate text-sm">{team.bio}</span>
                    )}
                  </span>
                  <Icon name="arrow-right" className="text-subtle ml-auto h-4 w-4 shrink-0" />
                </Link>
                {team.eventTypes.length > 0 && (
                  <ul className="border-subtle divide-subtle border-t [&>li+li]:border-t">
                    {team.eventTypes.map((eventType) => (
                      <li key={eventType.slug}>
                        <Link
                          href={`/team/${team.slug}/${eventType.slug}`}
                          className="bg-default hover:bg-subtle flex items-center justify-between px-4 py-3 pl-12 transition-colors">
                          <span className="min-w-0">
                            <span className="text-emphasis block truncate font-medium">
                              {eventType.title}
                            </span>
                            {eventType.description && (
                              <span className="text-subtle block truncate text-sm">
                                {eventType.description}
                              </span>
                            )}
                          </span>
                          <span className="text-subtle ml-4 flex shrink-0 items-center gap-2 text-sm">
                            {eventType.schedulingType && (
                              <Badge variant="gray">
                                {eventType.schedulingType === "ROUND_ROBIN"
                                  ? t("round_robin")
                                  : t("collective")}
                              </Badge>
                            )}
                            {t("x_minutes", { count: eventType.length })}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
